import {useState, useCallback, useMemo, useRef, useEffect} from 'react'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {Strong} from '@/components/shared/Strong'
import {useParams, useNavigate, useLocation} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    useDroppable,
} from '@dnd-kit/core'
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {
    ArrowLeft,
    GripVertical,
    Lock,
    Trash2,
    X,
    RefreshCw,
    Timer,
    Code,
    Loader2,
    ChevronRight,
    Plus,
    Play,
} from 'lucide-react'
import Editor from '@monaco-editor/react'
import {parseCronExpression} from '@/lib/cron-utils'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useToast} from '@/hooks/use-toast'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {permissions} from '@/lib/permissions'
import {Button} from '@/components/ui/button'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Card, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {Skeleton} from '@/components/ui/skeleton'
import {Textarea} from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {EnumSelectItems} from '@/components/shared/EnumSelectItems'
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {EMPTY_SEARCH_AND_FILTERS, type SearchAndFilters} from '@/hooks/use-server-pagination'
import type {SearchAndFilterContract} from '@/types/endpoint-contract'
import {EmptyState} from '@/components/shared/EmptyState'
import {CodeText} from '@/components/shared/CodeText'
import {HandlerType, JobSource, JobPriority, JobPriorityBadge} from '@/types/background-jobs'
import {ScriptOutboundConfigResponseFields} from '@/types/outbound-connector'
import type {
    JobBuilderData,
    TaskDefinition,
    TaskRequest,
} from '@/types/background-jobs'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {MutedText} from '@/components/shared/MutedText'
import {FormField} from '@/components/shared/FormField'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {ContentArea} from '@/components/shared/ContentArea'
import {DraggableRow} from '@/components/shared/DraggableRow'
import {StatusText} from '@/components/shared/StatusText'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {DropZone} from '@/components/shared/DropZone'

/* eslint-disable i18next/no-literal-string -- technical IDs, config keys, and component props */

// ============================================================================
// Types
// ============================================================================

interface BuilderTask {
    id: string
    name: string
    handlerType: HandlerType
    sortOrder: number
    handlerConfig: Record<string, unknown>
    skipOnFailure: boolean
    timeoutMs?: number
    retryCount?: number
    category?: string
    source: string | null
    isNew?: boolean
}

interface PaletteItem {
    id: string
    name: string
    handlerType: HandlerType
    category: string | null
    isPaletteItem: true
}

interface CreateJobFormData {
    name: string
    jobKey: string
    description: string
    cronExpression: string
    timeoutMs: number
    priority: string
}

const PALETTE_SEARCH_CONTRACT: SearchAndFilterContract = {
    sort: {},
    filter: {},
    search: {param: 'q'},
}

// ============================================================================
// sort_order Algorithm
// ============================================================================

function computeSortOrder(tasks: BuilderTask[], insertIndex: number): number {
    if (tasks.length === 0) return 100

    if (insertIndex <= 0) {
        return tasks[0].sortOrder - 50
    }
    if (insertIndex >= tasks.length) {
        return tasks[tasks.length - 1].sortOrder + 100
    }

    const before = tasks[insertIndex - 1].sortOrder
    const after = tasks[insertIndex].sortOrder
    const mid = Math.floor((before + after) / 2)

    // Gap exhaustion — midpoint equals one of the neighbors
    if (mid <= before || mid >= after) {
        return -1 // signal renumber needed
    }
    return mid
}

function renumberTasks(tasks: BuilderTask[]): BuilderTask[] {
    let nextOrder = 100
    return tasks.map(t => {
        const order = nextOrder
        nextOrder += 100
        return {...t, sortOrder: order}
    })
}

// ============================================================================
// Main Component
// ============================================================================

export function JobBuilderPage() {
    const services = useServices()
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const i18n = useI18n()
    const {toast} = useToast()

    const isCreateMode = id === 'new'
    const canAdmin = hasPermission(permissions.jobs.manage)

    usePageTitle(isCreateMode ? i18n.jobBuilder.newPageTitle : i18n.jobBuilder.editPageTitle)

    // State from create dialog (passed via location state)
    const createFormData = location.state as CreateJobFormData | null

    // Task chain state
    const [tasks, setTasks] = useState<BuilderTask[]>([])
    const [isDirty, setIsDirty] = useState(false)
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
    const cancelDialog = useDialog()
    const conflictDialog = useDialog()
    const [paletteSearch, setPaletteSearch] = useState<SearchAndFilters>(EMPTY_SEARCH_AND_FILTERS)
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
    // scriptDialogTaskId removed — create mode now auto-saves and navigates to full-page editor
    const initialLoadDone = useRef(false)

    // DnD state
    const [activeDragId, setActiveDragId] = useState<string | null>(null)
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    )

    // Fetch builder data for edit mode
    const {data: builderData, isLoading, error: builderError} = useQuery({
        queryKey: ['job-builder', id],
        queryFn: () => services.jobs.builder(id!) as Promise<JobBuilderData>,
        enabled: !isCreateMode && !!id,
    })

    // Fetch palette (STATIC tasks from the job's existing tasks for reference)
    const {data: paletteTasks, isLoading: paletteLoading, error: paletteError} = useQuery({
        queryKey: ['task-palette', id],
        queryFn: () => services.jobs.taskPalette(id!) as Promise<TaskDefinition[]>,
        enabled: !isCreateMode && !!id,
    })

    // Initialize tasks from builder data
    useEffect(() => {
        if (builderData && !initialLoadDone.current) {
            const loaded: BuilderTask[] = builderData.tasks.map(t => ({
                id: t.id,
                name: t.name,
                handlerType: t.handlerType as HandlerType,
                sortOrder: t.sortOrder,
                handlerConfig: t.handlerConfig,
                skipOnFailure: t.skipOnFailure,
                timeoutMs: t.timeoutMs ?? undefined,
                retryCount: t.retryCount ?? undefined,
                category: t.category ?? undefined,
                source: null, // will be enriched from paletteTasks
            }))
            setTasks(loaded)
            initialLoadDone.current = true
        }
    }, [builderData])

    // Enrich tasks with source info from palette data
    useEffect(() => {
        if (paletteTasks && initialLoadDone.current) {
            setTasks(prev => prev.map(t => {
                const paletteMatch = paletteTasks.find(p => p.id === t.id)
                return paletteMatch ? {...t, source: paletteMatch.source} : t
            }))
        }
    }, [paletteTasks])

    // Build palette items (distinct STATIC tasks grouped by category)
    const paletteItems = useMemo((): PaletteItem[] => {
        if (!paletteTasks) return []
        const staticTasks = paletteTasks.filter(t => t.source === JobSource.STATIC)
        const seen = new Set<string>()
        return staticTasks
            .filter(t => {
                const key = `${t.name}-${t.handlerType}`
                if (seen.has(key)) return false
                seen.add(key)
                return true
            })
            .map(t => ({
                id: `palette-${t.id}`,
                name: t.name,
                handlerType: t.handlerType as HandlerType,
                category: t.category,
                isPaletteItem: true as const,
            }))
    }, [paletteTasks])

    // Custom task palette items (always available)
    const customPaletteItems: PaletteItem[] = useMemo(() => [
        {id: 'palette-groovy', name: i18n.jobBuilder.groovyScript as string, handlerType: HandlerType.GROOVY_SCRIPT, category: null, isPaletteItem: true as const},
        {id: 'palette-delay', name: i18n.jobBuilder.delay as string, handlerType: HandlerType.DELAY, category: null, isPaletteItem: true as const},
    ], [i18n])

    // Filter palette
    const filteredPaletteItems = useMemo(() => {
        const filter = paletteSearch.q.toLowerCase()
        const matchFilter = (item: PaletteItem) => !filter || item.name.toLowerCase().includes(filter)
        return {
            registered: paletteItems.filter(matchFilter),
            custom: customPaletteItems.filter(matchFilter),
        }
    }, [paletteItems, customPaletteItems, paletteSearch])

    // Grouped palette items by category
    const groupedRegistered = useMemo(() => {
        const groups: Record<string, PaletteItem[]> = {}
        for (const item of filteredPaletteItems.registered) {
            const cat = item.category || 'General'
            if (!groups[cat]) groups[cat] = []
            groups[cat].push(item)
        }
        return groups
    }, [filteredPaletteItems.registered])

    const selectedTask = useMemo(() =>
        tasks.find(t => t.id === selectedTaskId) || null,
    [tasks, selectedTaskId])

    // Sorted tasks for display
    const sortedTasks = useMemo(() =>
        [...tasks].sort((a, b) => a.sortOrder - b.sortOrder),
    [tasks])

    // ========================================================================
    // Handlers
    // ========================================================================

    /**
     * Auto-save the job (create mode) and navigate to the full-page script editor.
     * Used when clicking "Edit Script" on a groovy task in a not-yet-saved job.
     * Accepts allTasks explicitly because React state may not be updated yet.
     */
    const saveAndOpenScriptEditor = useCallback(async (taskSortOrder: number, allTasks?: BuilderTask[]) => {
        if (!createFormData) return

        const tasksToSave = allTasks || sortedTasks
        const taskRequests: TaskRequest[] = tasksToSave.map(t => ({
            name: t.name,
            sortOrder: t.sortOrder,
            handlerType: t.handlerType,
            handlerConfig: t.handlerConfig,
            timeoutMs: t.timeoutMs,
            retryCount: t.retryCount,
            skipOnFailure: t.skipOnFailure,
            category: t.category,
        }))

        try {
            const jobResponse = await services.jobs.create({
                jobKey: createFormData.jobKey,
                name: createFormData.name,
                description: createFormData.description || null,
                cronExpression: createFormData.cronExpression || null,
                timeoutMs: createFormData.timeoutMs * 1000,
                tasks: taskRequests,
            })

            queryClient.invalidateQueries({queryKey: ['jobs']})

            // Find the saved task by sort order and handler type
            const tasksResponse = await services.jobs.tasks(jobResponse.id)
            const savedTask = tasksResponse.find(
                t => t.sortOrder === taskSortOrder && t.handlerType === HandlerType.GROOVY_SCRIPT
            )

            if (savedTask) {
                navigate(routes.backgroundJobs.builder.script(jobResponse.id, savedTask.id))
            } else {
                navigate(routes.backgroundJobs.builder.edit(jobResponse.id))
            }
        } catch {
            toast({description: i18n.jobBuilder.saveError, variant: 'destructive'})
        }
    }, [createFormData, sortedTasks, navigate, queryClient, toast, i18n, services.jobs])

    const addTaskFromPalette = useCallback((item: PaletteItem, insertIndex?: number) => {
        const newTask: BuilderTask = {
            id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            name: item.handlerType === HandlerType.GROOVY_SCRIPT
                ? i18n.jobBuilder.groovyScript as string
                : item.handlerType === HandlerType.DELAY
                    ? i18n.jobBuilder.delay as string
                    : item.name,
            handlerType: item.handlerType,
            sortOrder: 0, // will be calculated
            handlerConfig: item.handlerType === HandlerType.DELAY
                ? {durationSeconds: 60}
                : item.handlerType === HandlerType.GROOVY_SCRIPT
                    ? {script: '', inputs: {}, sqlWriteAccess: false}
                    : {},
            skipOnFailure: false,
            source: JobSource.DYNAMIC,
            isNew: true,
        }

        setTasks(prev => {
            const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder)
            const idx = insertIndex ?? sorted.length
            let sortOrder = computeSortOrder(sorted, idx)

            if (sortOrder === -1) {
                // Gap exhaustion — renumber first
                const renumbered = renumberTasks(sorted)
                sortOrder = computeSortOrder(renumbered, idx)
                const result = [...renumbered]
                result.splice(idx, 0, {...newTask, sortOrder})
                return result
            }

            const result = [...sorted]
            result.splice(idx, 0, {...newTask, sortOrder})
            return result
        })

        setIsDirty(true)

        // For GROOVY_SCRIPT tasks, open the script editor directly instead of the sidebar
        if (newTask.handlerType === HandlerType.GROOVY_SCRIPT) {
            if (isCreateMode) {
                // Build the full task list including the just-added task (state not yet updated)
                const sorted = [...tasks].sort((a, b) => a.sortOrder - b.sortOrder)
                const idx = insertIndex ?? sorted.length
                const allTasks = [...sorted]
                allTasks.splice(idx, 0, {...newTask, sortOrder: newTask.sortOrder})
                saveAndOpenScriptEditor(newTask.sortOrder, allTasks)
            } else {
                setSelectedTaskId(newTask.id)
            }
        } else {
            setSelectedTaskId(newTask.id)
        }
    }, [i18n, tasks, isCreateMode, saveAndOpenScriptEditor])

    const removeTask = useCallback((taskId: string) => {
        setTasks(prev => prev.filter(t => t.id !== taskId))
        if (selectedTaskId === taskId) setSelectedTaskId(null)
        setIsDirty(true)
    }, [selectedTaskId])

    const updateTask = useCallback((taskId: string, updates: Partial<BuilderTask>) => {
        setTasks(prev => prev.map(t => t.id === taskId ? {...t, ...updates} : t))
        setIsDirty(true)
        // Clear validation error for this task
        setValidationErrors(prev => {
            const next = {...prev}
            delete next[taskId]
            return next
        })
    }, [])

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveDragId(event.active.id as string)
    }, [])

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        setActiveDragId(null)
        const {active, over} = event
        if (!over || active.id === over.id) return

        // Check if dragging from palette
        const activeIdStr = active.id as string
        if (activeIdStr.startsWith('palette-')) {
            // Find the palette item
            const paletteItem = [...paletteItems, ...customPaletteItems]
                .find(p => p.id === activeIdStr)
            if (paletteItem) {
                const overIndex = sortedTasks.findIndex(t => t.id === over.id)
                addTaskFromPalette(paletteItem, overIndex >= 0 ? overIndex : undefined)
            }
            return
        }

        // Reorder within task chain — only DYNAMIC tasks
        const activeTask = tasks.find(t => t.id === activeIdStr)
        if (!activeTask || activeTask.source === JobSource.STATIC) return

        setTasks(prev => {
            const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder)
            const oldIndex = sorted.findIndex(t => t.id === active.id)
            const newIndex = sorted.findIndex(t => t.id === over.id)
            if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev

            // Remove from old position
            const [moved] = sorted.splice(oldIndex, 1)
            // Insert at new position
            sorted.splice(newIndex, 0, moved)

            // Recalculate sort_order for moved task
            const sortOrder = computeSortOrder(
                sorted.filter(t => t.id !== moved.id),
                sorted.indexOf(moved),
            )
            if (sortOrder === -1) {
                return renumberTasks(sorted)
            }

            return sorted.map(t => t.id === moved.id ? {...t, sortOrder} : t)
        })
        setIsDirty(true)
    }, [tasks, sortedTasks, paletteItems, customPaletteItems, addTaskFromPalette])

    // ========================================================================
    // Save / Cancel
    // ========================================================================

    const saveMutation = useEntityMutation({
        mutationFn: async () => {
            const taskRequests: TaskRequest[] = sortedTasks.map(t => ({
                name: t.name,
                sortOrder: t.sortOrder,
                handlerType: t.handlerType,
                handlerConfig: t.handlerConfig,
                timeoutMs: t.timeoutMs,
                retryCount: t.retryCount,
                skipOnFailure: t.skipOnFailure,
                category: t.category,
            }))

            if (isCreateMode && createFormData) {
                // Create job first, then save tasks
                const jobResponse = await services.jobs.create({
                    jobKey: createFormData.jobKey,
                    name: createFormData.name,
                    description: createFormData.description || null,
                    cronExpression: createFormData.cronExpression || null,
                    timeoutMs: createFormData.timeoutMs * 1000,
                    tasks: taskRequests,
                })
                return jobResponse
            }

            // Edit mode — update tasks
            await services.jobs.updateTasks(id!, {tasks: taskRequests})
            return null
        },
        invalidate: [
            ['jobs'],
            ['job', id],
            ['job-tasks', id],
        ],
        successMessage: i18n.jobBuilder.jobSaved,
        onSuccess: (result) => {
            setIsDirty(false)
            if (isCreateMode && result) {
                navigate(routes.backgroundJobs.jobs.detail(result.id))
            } else {
                navigate(routes.backgroundJobs.jobs.detail(id!))
            }
        },
        onError: (error: unknown) => {
            const err = error as {status?: number}
            if (err.status === 409) {
                conflictDialog.open()
            } else {
                toast({description: i18n.jobBuilder.saveError, variant: 'destructive'})
            }
        },
    })

    const handleSave = useCallback(() => {
        // Validate all DYNAMIC tasks have names
        const errors: Record<string, string> = {}
        for (const task of sortedTasks) {
            if (task.source !== JobSource.STATIC && !task.name.trim()) {
                errors[task.id] = i18n.jobBuilder.nameRequired as string
            }
            if (task.handlerType === HandlerType.DELAY) {
                const dur = (task.handlerConfig?.durationSeconds as number) || 0
                if (dur < 1 || dur > 86400) {
                    errors[task.id] = i18n.jobBuilder.durationOutOfRange as string
                }
            }
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors)
            return
        }

        saveMutation.mutate()
    }, [sortedTasks, saveMutation, i18n])

    // beforeunload guard for unsaved changes (browser back, refresh, close tab)
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [isDirty])

    const handleCancel = useCallback(() => {
        if (isDirty) {
            cancelDialog.open()
        } else {
            navigate(-1)
        }
    }, [isDirty, navigate, cancelDialog])

    const handleDiscard = useCallback(() => {
        cancelDialog.close()
        navigate(-1)
    }, [navigate, cancelDialog])

    // Permission guard
    if (!canAdmin) {
        navigate(routes.backgroundJobs.jobs.list())
        return null
    }

    // Loading state
    if (!isCreateMode && isLoading) {
        return (
            <Stack data-testid="builder-loading">
                <Skeleton className="h-10 w-full"/>
                <InlineRow gap="md" data-testid="builder-loading-panels">
                    <Skeleton className="h-[600px] w-[250px]"/>
                    <Skeleton className="h-[600px] flex-1"/>
                </InlineRow>
            </Stack>
        )
    }

    // Error state — GET /jobs/{id}/builder failed.
    if (!isCreateMode && builderError) {
        return (
            <Stack data-testid="builder-error">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="btn-back">
                    <Icon icon={ArrowLeft} className="mr-2"/>
                    {i18n.common.back}
                </Button>
                <ErrorBanner
                    error={builderError as Error}
                    onRetry={() => queryClient.invalidateQueries({queryKey: ['job-builder', id]})}
                />
            </Stack>
        )
    }

    const dynamicTasks = sortedTasks.filter(t => t.source !== JobSource.STATIC)
    const hasTasks = sortedTasks.length > 0

    return (
        <Stack data-testid="job-builder-page">
            {/* Toolbar */}
            <InlineRow justify="between">
                <InlineRow gap="lg">
                    <Button variant="ghost" size="sm" onClick={handleCancel} data-testid="btn-back">
                        <Icon icon={ArrowLeft} className="mr-2"/>
                        {i18n.common.back}
                    </Button>
                    {isDirty && (
                        <Badge variant="outline" className="text-status-warning border-amber-300" data-testid="badge-unsaved">
                            {i18n.jobBuilder.unsavedChanges}
                        </Badge>
                    )}
                </InlineRow>
                <InlineRow>
                    {!hasTasks && (
                        <MutedText size="xs" className="mr-1" data-testid="hint-no-tasks">
                            {i18n.jobBuilder.addTaskBeforeSaving}
                        </MutedText>
                    )}
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        data-testid="btn-cancel"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!hasTasks || saveMutation.isPending}
                        data-testid="btn-save"
                    >
                        {saveMutation.isPending ? (
                            <>
                                <ButtonSpinner/>
                                {i18n.jobBuilder.savingJob}
                            </>
                        ) : (
                            i18n.jobBuilder.saveJob
                        )}
                    </Button>
                </InlineRow>
            </InlineRow>

            {/* Two-panel layout */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <InlineRow gap="md" className="!items-stretch min-h-[calc(100vh-10rem)]">
                    {/* Left panel — Palette (250px) */}
                    <Stack className="w-[250px] shrink-0" data-testid="task-palette">
                        <Card className="h-full">
                            <CardContent className="p-3 space-y-3">
                                <Strong className="text-sm">{i18n.jobBuilder.taskPalette}</Strong>

                                {/* Search */}
                                <UnifiedSearchBar
                                    contract={PALETTE_SEARCH_CONTRACT}
                                    value={paletteSearch}
                                    onChange={(next) => setPaletteSearch(prev => ({...prev, ...next}))}
                                    placeholder={i18n.jobBuilder.searchTasks as string}
                                    testIdNamespace="palette"
                                />

                                {/* Registered Tasks */}
                                {!isCreateMode && (
                                    <div>
                                        <MutedText size="xs" className="font-medium mb-1">{i18n.jobBuilder.registeredTasks}</MutedText>
                                        {paletteLoading ? (
                                            <Stack gap="sm" data-testid="palette-loading">
                                                {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full"/>)}
                                            </Stack>
                                        ) : paletteError ? (
                                            <Stack gap="xs" data-testid="palette-error">
                                                <MutedText size="xs">{i18n.jobBuilder.failedToLoadPalette}</MutedText>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="mt-1"
                                                    onClick={() => queryClient.invalidateQueries({queryKey: ['task-palette']})}
                                                    data-testid="btn-retry-palette"
                                                >
                                                    <Icon icon={RefreshCw} size="xs" className="mr-1"/>
                                                    {i18n.backgroundJobs.retry}
                                                </Button>
                                            </Stack>
                                        ) : Object.keys(groupedRegistered).length === 0 ? (
                                            <MutedText size="xs" className="py-2" data-testid="palette-empty">
                                                {i18n.jobBuilder.noRegisteredTasks}
                                            </MutedText>
                                        ) : (
                                            Object.entries(groupedRegistered).map(([category, items]) => (
                                                <Stack key={category} gap="xs">
                                                    <MutedText size="xs" className="font-medium">{category}</MutedText>
                                                    {items.map(item => (
                                                        <DraggablePaletteItem key={item.id} item={item} onAdd={() => addTaskFromPalette(item)}/>
                                                    ))}
                                                </Stack>
                                            ))
                                        )}
                                    </div>
                                )}

                                {/* Custom Tasks */}
                                <div>
                                    <MutedText size="xs" className="font-medium mb-1">{i18n.jobBuilder.customTasks}</MutedText>
                                    {filteredPaletteItems.custom.map(item => (
                                        <DraggablePaletteItem key={item.id} item={item} onAdd={() => addTaskFromPalette(item)}/>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </Stack>

                    {/* Right panel — Task Chain Editor */}
                    <ContentArea data-testid="task-chain-editor">
                        <Card className="h-full">
                            <CardContent className="p-4">
                                <Strong className="text-sm">{i18n.jobBuilder.taskChain}</Strong>

                                {sortedTasks.length === 0 ? (
                                    <TaskChainDropZone>
                                        <EmptyState
                                            icon={Plus}
                                            title={i18n.jobBuilder.noTasksYet as string}
                                            description={i18n.jobBuilder.addTasksFromPalette as string}
                                            data-testid="empty-chain"
                                        />
                                    </TaskChainDropZone>
                                ) : (
                                    <SortableContext items={dynamicTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                                        <Stack gap="xs">
                                            {sortedTasks.map((task, taskIndex) => (
                                                <SortableTaskItem
                                                    key={task.id}
                                                    task={task}
                                                    taskIndex={taskIndex}
                                                    isSelected={task.id === selectedTaskId}
                                                    error={validationErrors[task.id]}
                                                    onSelect={() => {
                                                        // For GROOVY_SCRIPT tasks, open the script editor directly
                                                        if (task.handlerType === HandlerType.GROOVY_SCRIPT && task.source !== JobSource.STATIC) {
                                                            if (isCreateMode) {
                                                                saveAndOpenScriptEditor(task.sortOrder)
                                                            } else {
                                                                navigate(routes.backgroundJobs.builder.script(id!, task.id))
                                                            }
                                                        } else {
                                                            setSelectedTaskId(task.id)
                                                        }
                                                    }}
                                                    onRemove={() => removeTask(task.id)}
                                                    canAdmin={canAdmin}
                                                    onEditScript={
                                                        task.handlerType === HandlerType.GROOVY_SCRIPT && task.source !== JobSource.STATIC
                                                            ? () => {
                                                                if (isCreateMode) {
                                                                    saveAndOpenScriptEditor(task.sortOrder)
                                                                } else {
                                                                    navigate(routes.backgroundJobs.builder.script(id!, task.id))
                                                                }
                                                            }
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                        </Stack>
                                    </SortableContext>
                                )}
                            </CardContent>
                        </Card>
                    </ContentArea>
                </InlineRow>

                <DragOverlay>
                    {activeDragId ? (
                        <Card className="shadow-lg opacity-80">
                            {(() => {
                                const task = tasks.find(t => t.id === activeDragId)
                                const paletteItem = [...paletteItems, ...customPaletteItems].find(p => p.id === activeDragId)
                                return task?.name || paletteItem?.name || ''
                            })()}
                        </Card>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Task Config Panel (Sheet) */}
            <Sheet open={!!selectedTask} onOpenChange={(open) => { if (!open) setSelectedTaskId(null) }}>
                <SheetContent className="w-[400px] sm:w-[400px]" data-testid="task-config-panel"> {selectedTask && (
                        <TaskConfigPanel
                            task={selectedTask}
                            onUpdate={(updates) => updateTask(selectedTask.id, updates)}
                            onClose={() => setSelectedTaskId(null)}
                            i18n={i18n}
                        />
                    )}
                </SheetContent>
            </Sheet>


            {/* Cancel Confirmation Dialog */}
            <AlertDialog open={cancelDialog.isOpen} onOpenChange={cancelDialog.bind.onOpenChange}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.jobBuilder.discardChangesTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{i18n.jobBuilder.discardChangesDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-keep-editing">{i18n.jobBuilder.keepEditing}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDiscard}
                            data-testid="btn-discard"
                        >
                            {i18n.jobBuilder.discard}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Conflict Dialog */}
            <AlertDialog open={conflictDialog.isOpen} onOpenChange={conflictDialog.bind.onOpenChange}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.jobBuilder.conflictTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{i18n.jobBuilder.conflictDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-conflict-cancel">{i18n.common.cancel}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                conflictDialog.close()
                                queryClient.invalidateQueries({queryKey: ['job-builder', id]})
                                initialLoadDone.current = false
                            }}
                            data-testid="btn-conflict-reload"
                        >
                            {i18n.jobBuilder.reload}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Stack>
    )
}

// ============================================================================
// Palette Item (draggable)
// ============================================================================

function DraggablePaletteItem({item, onAdd}: {item: PaletteItem; onAdd: () => void}) {
    const {attributes, listeners, setNodeRef, transform, isDragging} = useSortable({
        id: item.id,
        data: {type: 'palette', item},
    })

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
    }

    const icon = item.handlerType === HandlerType.GROOVY_SCRIPT
        ? <Icon icon={Code} size="xs"/>
        : item.handlerType === HandlerType.DELAY
            ? <Icon icon={Timer} size="xs"/>
            : <Icon icon={ChevronRight} size="xs"/>

    return (
        <DraggableRow
            variant="palette"
            innerRef={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            data-testid={`palette-item-${item.id}`}
        >
            <Icon icon={GripVertical} size="xs" muted/>
            {icon}
            <Strong truncate>{item.name}</Strong>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAdd() }}
                data-testid={`btn-add-${item.id}`}
            >
                <Icon icon={Plus} size="xs" className="text-muted-foreground hover:text-foreground"/>
            </button>
        </DraggableRow>
    )
}

// ============================================================================
// Task Chain Item (sortable)
// ============================================================================

function SortableTaskItem({task, taskIndex, isSelected, error, onSelect, onRemove, onEditScript, canAdmin}: {
    task: BuilderTask
    taskIndex: number
    isSelected: boolean
    error?: string
    onSelect: () => void
    onRemove: () => void
    onEditScript?: () => void
    canAdmin: boolean
}) {
    const isStatic = task.source === JobSource.STATIC
    const i18n = useI18n()
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: task.id,
        disabled: isStatic,
    })

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const handlerIcon = task.handlerType === HandlerType.GROOVY_SCRIPT
        ? <Icon icon={Code} size="sm"/>
        : task.handlerType === HandlerType.DELAY
            ? <Icon icon={Timer} size="sm"/>
            : <Icon icon={ChevronRight} size="sm"/>

    const showEditScript = canAdmin && task.handlerType === HandlerType.GROOVY_SCRIPT && !isStatic

    return (
        <DraggableRow
            variant="chain"
            locked={isStatic}
            selected={isSelected}
            hasError={Boolean(error)}
            dragging={isDragging}
            innerRef={setNodeRef}
            style={style}
            onClick={onSelect}
            data-testid={`task-item-${taskIndex}`}
        >
            {/* Drag handle or lock icon */}
            {isStatic ? (
                <Icon icon={Lock} muted data-testid={`lock-${taskIndex}`}/>
            ) : (
                <span {...attributes} {...listeners} data-testid={`drag-handle-${taskIndex}`}>
                    <Icon icon={GripVertical} muted className="cursor-grab"/>
                </span>
            )}

            {handlerIcon}
            <Strong truncate>{task.name}</Strong>
            <Badge variant="outline" className="text-xs shrink-0">{task.handlerType}</Badge>

            {/* Edit Script button — GROOVY_SCRIPT, DYNAMIC only, with jobs:manage */}
            {showEditScript && onEditScript && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onEditScript() }}
                    data-testid={`btn-edit-script-${taskIndex}`}
                >
                    <Icon icon={Code} size="xs" className="mr-1"/>
                    {i18n.jobBuilder.editScript}
                </Button>
            )}

            {/* Delete button — DYNAMIC only */}
            {!isStatic && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onRemove() }}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`btn-delete-task-${task.id}`}
                >
                    <Icon icon={Trash2} size="md"/>
                </Button>
            )}
        </DraggableRow>
    )
}

// ============================================================================
// Task Chain Drop Zone (for empty state)
// ============================================================================

function TaskChainDropZone({children}: {children: React.ReactNode}) {
    const {setNodeRef, isOver} = useDroppable({id: 'task-chain-drop-zone'})

    return (
        <DropZone innerRef={setNodeRef} active={isOver}>
            {children}
        </DropZone>
    )
}

// ============================================================================
// Task Config Panel
// ============================================================================

function TaskConfigPanel({task, onUpdate, onClose, i18n}: {
    task: BuilderTask
    onUpdate: (updates: Partial<BuilderTask>) => void
    onClose: () => void
    i18n: ReturnType<typeof useI18n>
}) {
    const isStatic = task.source === JobSource.STATIC
    const isReadOnly = isStatic

    return (
        <>
            <SheetHeader>
                <SheetTitle>
                    <InlineRow>
                        <Strong truncate>{task.name}</Strong>
                        <Badge variant="outline" className="text-xs shrink-0">{task.handlerType}</Badge>
                    </InlineRow>
                </SheetTitle>
            </SheetHeader>

            <ScrollableBox flush className="mt-4 max-h-[calc(100vh-120px)]">
                <Stack>
                {isReadOnly && (
                    <MutedText size="xs" data-testid="config-readonly-hint">
                        {i18n.jobBuilder.readOnlyConfig}
                    </MutedText>
                )}

                {task.handlerType === HandlerType.BEAN_METHOD && (
                    <BeanMethodConfig task={task} i18n={i18n}/>
                )}

                {task.handlerType === HandlerType.GROOVY_SCRIPT && (
                    <GroovyScriptConfig task={task} onUpdate={onUpdate} isReadOnly={isReadOnly} i18n={i18n}/>
                )}

                {task.handlerType === HandlerType.DELAY && (
                    <DelayConfig task={task} onUpdate={onUpdate} isReadOnly={isReadOnly} i18n={i18n}/>
                )}

                {/* Apply button for editable tasks */}
                {!isReadOnly && (
                    <Stack className="pt-2 border-t">
                        <Button
                            onClick={onClose}
                            className="w-full"
                            data-testid="btn-apply-config"
                        >
                            {i18n.jobBuilder.apply}
                        </Button>
                    </Stack>
                )}
                </Stack>
            </ScrollableBox>
        </>
    )
}

// ============================================================================
// BEAN_METHOD Config (read-only)
// ============================================================================

function BeanMethodConfig({task, i18n}: {task: BuilderTask; i18n: ReturnType<typeof useI18n>}) {
    const config = task.handlerConfig || {}
    return (
        <Stack gap="sm" data-testid="config-bean-method">
            <Stack gap="xs">
                <MutedText size="xs">{i18n.jobBuilder.beanName}</MutedText>
                <CodeText>{(config.beanName as string) || '—'}</CodeText>
            </Stack>
            <Stack gap="xs">
                <MutedText size="xs">{i18n.jobBuilder.methodName}</MutedText>
                <CodeText>{(config.methodName as string) || '—'}</CodeText>
            </Stack>
        </Stack>
    )
}

// ============================================================================
// GROOVY_SCRIPT Config
// ============================================================================

function GroovyScriptConfig({task, onUpdate, isReadOnly, i18n}: {
    task: BuilderTask
    onUpdate: (updates: Partial<BuilderTask>) => void
    isReadOnly: boolean
    i18n: ReturnType<typeof useI18n>
}) {
    const services = useServices()
    const config = task.handlerConfig || {}
    const script = (config.script as string) || ''
    const inputs = (config.inputs as Record<string, string>) || {}
    const sqlWriteAccess = (config.sqlWriteAccess as boolean) || false

    const [testOutput, setTestOutput] = useState<string | null>(null)
    const [testError, setTestError] = useState<string | null>(null)
    const [isTesting, setIsTesting] = useState(false)

    const updateConfig = (key: string, value: unknown) => {
        onUpdate({handlerConfig: {...config, [key]: value}})
    }

    const handleTestScript = async () => {
        setIsTesting(true)
        setTestOutput(null)
        setTestError(null)
        try {
            const result = await services.jobs.testScript(task.id, {script, inputs, sqlWriteAccess}) as {output: string; error?: string}
            if (result.error) {
                setTestError(result.error)
            } else {
                setTestOutput(result.output || 'OK')
            }
        } catch (err) {
            setTestError(i18n.jobBuilder.testScriptError as string)
            throw err
        } finally {
            setIsTesting(false)
        }
    }

    return (
        <Stack data-testid="config-groovy-script">
            {/* Name */}
            <div>
                <Label htmlFor="task-name">{i18n.jobBuilder.taskName}</Label>
                <Input
                    id="task-name"
                    value={task.name}
                    onChange={e => onUpdate({name: e.target.value})}
                    maxLength={100}
                    disabled={isReadOnly}
                    placeholder={i18n.jobBuilder.taskNamePlaceholder as string}
                    data-testid="input-task-name"
                />
            </div>

            {/* Script editor */}
            <div>
                <Label>{i18n.jobBuilder.scriptBody}</Label>
                <ScrollableBox autoHeight hideOverflow className="min-h-[200px] mt-1">
                    <Editor
                        height={200}
                        language="groovy"
                        theme="vs-dark"
                        value={script}
                        onChange={(val) => updateConfig(ScriptOutboundConfigResponseFields.script, val || '')}
                        options={{
                            readOnly: isReadOnly,
                            minimap: {enabled: false},
                            lineNumbers: 'on',
                            fontSize: 13,
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                        }}
                        data-testid="editor-script"
                    />
                </ScrollableBox>
            </div>

            {/* Inputs key-value editor */}
            <div>
                <Label>{i18n.jobBuilder.inputs}</Label>
                <Stack gap="xs" className="mt-1">
                    {Object.entries(inputs).map(([key, value], idx) => (
                        <InlineRow key={idx}>
                            <Input
                                placeholder={i18n.jobBuilder.inputKey as string}
                                value={key}
                                onChange={e => {
                                    const newInputs = {...inputs}
                                    delete newInputs[key]
                                    newInputs[e.target.value] = value
                                    updateConfig('inputs', newInputs)
                                }}
                                className="flex-1"
                                disabled={isReadOnly}
                                data-testid={`input-key-${idx}`}
                            />
                            <Input
                                placeholder={i18n.jobBuilder.inputValue as string}
                                value={value}
                                onChange={e => updateConfig('inputs', {...inputs, [key]: e.target.value})}
                                className="flex-1"
                                disabled={isReadOnly}
                                data-testid={`input-value-${idx}`}
                            />
                            {!isReadOnly && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        const newInputs = {...inputs}
                                        delete newInputs[key]
                                        updateConfig('inputs', newInputs)
                                    }}
                                    data-testid={`btn-remove-input-${idx}`}
                                >
                                    <X className="h-3 w-3"/>
                                </Button>
                            )}
                        </InlineRow>
                    ))}
                    {!isReadOnly && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => updateConfig('inputs', {...inputs, '': ''})}
                            data-testid="btn-add-input"
                        >
                            <Icon icon={Plus} size="xs" className="mr-1"/>
                            {i18n.jobBuilder.addInput}
                        </Button>
                    )}
                </Stack>
            </div>

            {/* SQL Write Access */}
            <InlineRow gap="lg" className="items-start">
                <Switch
                    checked={sqlWriteAccess}
                    onCheckedChange={(val) => updateConfig('sqlWriteAccess', val)}
                    disabled={isReadOnly}
                    data-testid="switch-sql-write-access"
                />
                <Stack gap="xs">
                    <Label>{i18n.jobBuilder.sqlWriteAccess}</Label>
                    <StatusText tone="warning" size="xs">{i18n.jobBuilder.sqlWriteAccessWarning}</StatusText>
                </Stack>
            </InlineRow>

            {/* Test Script */}
            {!isReadOnly && (
                <div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestScript}
                        disabled={isTesting || !script.trim()}
                        data-testid="btn-test-script"
                    >
                        {isTesting ? (
                            <>
                                <Icon icon={Loader2} size="xs" className="mr-1 animate-spin"/>
                                {i18n.jobBuilder.testScriptRunning}
                            </>
                        ) : (
                            <>
                                <Icon icon={Play} size="xs" className="mr-1"/>
                                {i18n.jobBuilder.testScript}
                            </>
                        )}
                    </Button>
                    {testOutput && (
                        <CodeBlock className="mt-2 max-h-32" data-testid="test-output">
                            {testOutput}
                        </CodeBlock>
                    )}
                    {testError && (
                        <CodeBlock variant="error" className="mt-2 max-h-32" data-testid="test-error">
                            {testError}
                        </CodeBlock>
                    )}
                </div>
            )}
        </Stack>
    )
}

// ============================================================================
// DELAY Config
// ============================================================================

function DelayConfig({task, onUpdate, isReadOnly, i18n}: {
    task: BuilderTask
    onUpdate: (updates: Partial<BuilderTask>) => void
    isReadOnly: boolean
    i18n: ReturnType<typeof useI18n>
}) {
    const config = task.handlerConfig || {}
    const durationSeconds = (config.durationSeconds as number) || 60

    return (
        <Stack data-testid="config-delay">
            {/* Name */}
            <div>
                <Label htmlFor="delay-name">{i18n.jobBuilder.taskName}</Label>
                <Input
                    id="delay-name"
                    value={task.name}
                    onChange={e => onUpdate({name: e.target.value})}
                    maxLength={100}
                    disabled={isReadOnly}
                    placeholder={i18n.jobBuilder.taskNamePlaceholder as string}
                    data-testid="input-delay-name"
                />
            </div>

            <FormField
                label={i18n.jobBuilder.durationSeconds as string}
                htmlFor="delay-duration"
                error={(durationSeconds < 1 || durationSeconds > 86400) ? (i18n.jobBuilder.durationOutOfRange as string) : undefined}
            >
                <Input
                    id="delay-duration"
                    type="number"
                    min={1}
                    max={86400}
                    value={durationSeconds}
                    onChange={e => {
                        const val = parseInt(e.target.value, 10) || 0
                        onUpdate({handlerConfig: {...config, durationSeconds: val}})
                    }}
                    disabled={isReadOnly}
                    data-testid="input-delay-duration"
                />
                <MutedText size="xs" className="mt-1 block">{i18n.jobBuilder.durationMin}{' · '}{i18n.jobBuilder.durationMax}</MutedText>
            </FormField>
        </Stack>
    )
}

// ============================================================================
// Create Job Dialog
// ============================================================================

export function CreateJobDialog({open, onClose}: {open: boolean; onClose: () => void}) {
    const navigate = useNavigate()
    const i18n = useI18n()
    const [formData, setFormData] = useState<CreateJobFormData>({
        name: '',
        jobKey: '',
        description: '',
        cronExpression: '',
        timeoutMs: 3600,
        priority: JobPriority.NORMAL,
    })
    const [errors, setErrors] = useState<Record<string, string>>({})

    const cronParsed = useMemo(() => parseCronExpression(formData.cronExpression, {locale: 'en'}), [formData.cronExpression])
    const cronDescription = cronParsed.description

    const cronInvalid = formData.cronExpression.trim() !== '' && cronDescription === null

    const validate = (): boolean => {
        const errs: Record<string, string> = {}
        if (!formData.name.trim()) errs.name = i18n.jobBuilder.nameRequired as string
        if (formData.name.length > 100) errs.name = i18n.jobBuilder.nameMaxLength as string
        if (!formData.jobKey.trim()) errs.jobKey = i18n.jobBuilder.keyRequired as string
        if (formData.jobKey.length > 100) errs.jobKey = i18n.jobBuilder.keyMaxLength as string
        if (formData.description.length > 500) errs.description = i18n.jobBuilder.descriptionMaxLength as string
        if (cronInvalid) errs.cronExpression = i18n.jobBuilder.cronInvalid as string
        setErrors(errs)
        return Object.keys(errs).length === 0
    }

    const handleNext = () => {
        if (!validate()) return
        onClose()
        navigate(routes.backgroundJobs.builder.newJob(), {state: formData})
    }

    const handleClose = () => {
        setFormData({name: '', jobKey: '', description: '', cronExpression: '', timeoutMs: 3600, priority: JobPriority.NORMAL})
        setErrors({})
        onClose()
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
            <DialogContent className="sm:max-w-[500px]" data-testid="create-job-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.jobBuilder.createJob}</DialogTitle>
                    <DialogDescription>{i18n.jobBuilder.createJobDescription}</DialogDescription>
                </DialogHeader>

                <DialogBody>
                <Stack className="py-2">
                    <FormField label={i18n.jobBuilder.jobName as string} htmlFor="new-job-name" error={errors.name}>
                        <Input
                            id="new-job-name"
                            value={formData.name}
                            onChange={e => setFormData(prev => ({...prev, name: e.target.value}))}
                            maxLength={100}
                            placeholder={i18n.jobBuilder.jobNamePlaceholder as string}
                            data-testid="input-job-name"
                        />
                    </FormField>

                    <FormField label={i18n.jobBuilder.jobKey as string} htmlFor="new-job-key" error={errors.jobKey}>
                        <Input
                            id="new-job-key"
                            value={formData.jobKey}
                            onChange={e => setFormData(prev => ({...prev, jobKey: e.target.value}))}
                            maxLength={100}
                            placeholder={i18n.jobBuilder.jobKeyPlaceholder as string}
                            data-testid="input-job-key"
                        />
                    </FormField>

                    <FormField label={i18n.jobBuilder.jobDescription as string} htmlFor="new-job-desc" error={errors.description}>
                        <Textarea
                            id="new-job-desc"
                            value={formData.description}
                            onChange={e => setFormData(prev => ({...prev, description: e.target.value}))}
                            maxLength={500}
                            placeholder={i18n.jobBuilder.jobDescriptionPlaceholder as string}
                            rows={2}
                            data-testid="input-job-description"
                        />
                    </FormField>

                    {/* Cron Expression */}
                    <FormField label={i18n.jobBuilder.cronExpression as string} htmlFor="new-job-cron" error={cronInvalid ? (i18n.jobBuilder.cronInvalid as string) : undefined}>
                        <Input
                            id="new-job-cron"
                            value={formData.cronExpression}
                            onChange={e => setFormData(prev => ({...prev, cronExpression: e.target.value}))}
                            placeholder={i18n.jobBuilder.cronExpressionPlaceholder as string}
                            data-testid="input-cron-expression"
                        />
                        {cronDescription && (
                            <MutedText size="xs" className="mt-1 text-status-success" data-testid="cron-preview">
                                {i18n.jobBuilder.cronPreview({description: cronDescription})}
                            </MutedText>
                        )}
                        <MutedText size="xs" className="mt-1 block">{i18n.jobBuilder.cronExpressionHint}</MutedText>
                    </FormField>

                    {/* Timeout */}
                    <div>
                        <Label htmlFor="new-job-timeout">{i18n.jobBuilder.timeoutSeconds}</Label>
                        <Input
                            id="new-job-timeout"
                            type="number"
                            min={1}
                            max={86400}
                            value={formData.timeoutMs}
                            onChange={e => setFormData(prev => ({...prev, timeoutMs: parseInt(e.target.value, 10) || 3600}))}
                            data-testid="input-timeout"
                        />
                        <MutedText size="xs" className="mt-1 block">{i18n.jobBuilder.timeoutDefault}</MutedText>
                    </div>

                    {/* Priority */}
                    <div>
                        <Label>{i18n.jobBuilder.priority}</Label>
                        <Select
                            value={formData.priority}
                            onValueChange={val => setFormData(prev => ({...prev, priority: val}))}
                        >
                            <SelectTrigger data-testid="select-priority">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                <EnumSelectItems enumClass={JobPriorityBadge}/>
                            </SelectContent>
                        </Select>
                    </div>
                </Stack>
                </DialogBody>

                <SaveCancelFooter
                    testIdPrefix="create"
                    onCancel={handleClose}
                    saveLabel={i18n.jobBuilder.next}
                    saveType="button"
                    onSave={handleNext}
                />
            </DialogContent>
        </Dialog>
    )
}
